import { useState, useEffect } from "react";
import { Truck, Loader2, CheckCircle2 } from "lucide-react";
import { getShippingRates } from "../lib/api";

const COUNTRIES = [
  { code: "CA", name: "Canada" },
  { code: "US", name: "United States" },
  { code: "GB", name: "United Kingdom" },
  { code: "FR", name: "France" },
  { code: "DE", name: "Germany" },
  { code: "IT", name: "Italy" },
  { code: "ES", name: "Spain" },
  { code: "NL", name: "Netherlands" },
  { code: "IE", name: "Ireland" },
];

export default function ShippingEstimator({ items, symbol, selected, onSelect }) {
  const [country, setCountry] = useState("CA");
  const [province, setProvince] = useState("");
  const [postal, setPostal] = useState("");
  const [loading, setLoading] = useState(false);
  const [options, setOptions] = useState(null);
  const [unavailable, setUnavailable] = useState(false);

  const fetchRates = async () => {
    if (!postal.trim()) return;
    setLoading(true);
    setUnavailable(false);
    onSelect(null);
    try {
      const res = await getShippingRates({
        country,
        province,
        postal_code: postal,
        items: items.map((i) => ({ product_id: i.id, quantity: i.quantity })),
      });
      if (res.ok && res.options?.length) {
        setOptions(res.options);
      } else {
        setOptions([]);
        setUnavailable(true); // live rates unavailable → standard shipping at no extra cost
      }
    } catch {
      setOptions([]);
      setUnavailable(true);
    } finally {
      setLoading(false);
    }
  };

  // Auto-load rates once the destination looks complete (keeps the manual button too).
  useEffect(() => {
    const p = postal.trim().replace(/\s/g, "");
    const ready = country === "CA" ? p.length >= 6 : p.length >= 4;
    if (!ready) return;
    const t = setTimeout(() => { fetchRates(); }, 700);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [country, province, postal]);

  return (
    <div className="space-y-2.5" data-testid="shipping-estimator">
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] font-semibold text-zinc-500">
        <Truck size={13} strokeWidth={1.8} className="luxe-text" />
        Live shipping rates
      </div>

      <div className="grid grid-cols-2 gap-2">
        <select
          value={country}
          onChange={(e) => { setCountry(e.target.value); setOptions(null); setUnavailable(false); onSelect(null); }}
          data-testid="ship-country"
          className="col-span-2 border border-zinc-300 h-9 px-2 text-xs bg-white focus:outline-none focus:border-zinc-900"
        >
          {COUNTRIES.map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
        </select>
        <input
          value={province}
          onChange={(e) => setProvince(e.target.value)}
          placeholder="Province / State"
          data-testid="ship-province"
          className="border border-zinc-300 h-9 px-2 text-xs focus:outline-none focus:border-zinc-900"
        />
        <input
          value={postal}
          onChange={(e) => setPostal(e.target.value)}
          placeholder="Postal / ZIP"
          data-testid="ship-postal"
          className="border border-zinc-300 h-9 px-2 text-xs uppercase focus:outline-none focus:border-zinc-900"
        />
      </div>

      <button
        type="button"
        onClick={fetchRates}
        disabled={loading || !postal.trim()}
        data-testid="get-rates-btn"
        className="w-full border border-zinc-900 h-9 text-[10px] uppercase tracking-[0.2em] font-bold hover:bg-zinc-900 hover:text-white transition disabled:opacity-40 inline-flex items-center justify-center gap-2"
      >
        {loading ? <><Loader2 size={12} className="animate-spin" /> Getting rates</> : "Get live rates"}
      </button>

      {options && options.length > 0 && (
        <div className="space-y-1.5 pt-1" data-testid="shipping-options">
          {options.map((o) => {
            const active = selected?.service_code === o.service_code;
            return (
              <button
                key={o.service_code}
                type="button"
                onClick={() => onSelect({ service_code: o.service_code, service_name: o.service_name, amount_cad: o.price })}
                data-testid={`ship-option-${o.service_code}`}
                className={`w-full flex items-center justify-between gap-3 px-3 py-2 border text-left transition ${active ? "border-zinc-900 bg-white" : "border-zinc-200 hover:border-zinc-400"}`}
              >
                <span className="flex items-center gap-2 min-w-0">
                  {active && <CheckCircle2 size={14} className="luxe-text shrink-0" />}
                  <span className="text-[11px] font-semibold truncate">{o.service_name}</span>
                </span>
                <span className="text-xs font-mono font-bold shrink-0">{symbol}{o.price.toFixed(2)}</span>
              </button>
            );
          })}
        </div>
      )}

      {unavailable && (
        <p className="text-[10px] text-zinc-500 leading-relaxed" data-testid="rates-unavailable">
          Live shipping rates are temporarily unavailable — you can still check out and standard shipping will be arranged at no extra charge.
        </p>
      )}
    </div>
  );
}
